import {
    Box, Text, Flex, Input, Textarea, Spinner,
    Button, IconButton, Tooltip, VStack
} from "@chakra-ui/react";
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { FiEye, FiArrowDown, FiArrowUp } from 'react-icons/fi';
import UsersService from '../../services/users/usersService';
import { useAppToast } from "../../hooks/useAppToast";
import { useTranslation } from "react-i18next";

const UserDetailPage = () => {
    const { t } = useTranslation("default");
    const { userId } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [eventsLoading, setEventsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [sort, setSort] = useState('startDate');
    const [sortDir, setSortDir] = useState('desc');
    const toast = useAppToast();

    useEffect(() => {
        const fetchUserDetails = async () => {
            try {
                const res = await UsersService.getUserById(userId);
                setUser(res.data.data);
            } catch (error) {
                toast({
                    title: t('user.error.fetch_details'),
                    status: 'error',
                    duration: 3000,
                    isClosable: true
                });
            } finally {
                setLoading(false);
            }
        };

        fetchUserDetails();
    }, [userId]);

    useEffect(() => {
        const fetchUserEvents = async () => {
            setEventsLoading(true);
            try {
                const res = await UsersService.getUserEvents(userId, {
                    page: currentPage
                });

                // Try to access the items - check different response structures
                const items = res.data?.data?.items || res.data?.items || [];
                const totalPages = res.data?.data?.totalPages || res.data?.totalPages || 1;

                setEvents(items);
                setTotalPages(totalPages);
            } catch (error) {
                toast({
                    title: t('user.error.fetch_events'),
                    status: 'error',
                    duration: 3000,
                    isClosable: true
                });
            } finally {
                setEventsLoading(false);
            }
        };

        if (userId) {
            fetchUserEvents();
        }
    }, [userId, currentPage]);

    const handleSort = (column) => {
        if (sort === column) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSort(column);
            setSortDir('asc');
        }
    };

    const renderSortIcon = (key) => {
        if (sort !== key) return null;
        return sortDir === 'asc' ? <FiArrowUp /> : <FiArrowDown />;
    };

    if (loading) {
        return (
            <Flex justify="center" align="center" minH="100vh">
                <Spinner size="xl" />
            </Flex>
        );
    }

    return (
        <Box w="100%" maxW={"85rem"} py={'80px'} px={4} gap={'32px'} display="flex" flexDirection="column">
            {/* Nagłówek strony */}
            <Flex flexDirection="column" gap={4}>
                <Button
                    onClick={() => navigate('/users')}
                    alignSelf="flex-start"
                    variant="ghost"
                    leftIcon={<Text as="span">←</Text>}
                >
                    {t('user.action.back_to_list')}
                </Button>
                <Flex flexDirection="column" className="title" gap={'16px'}>
                    <Text fontSize="2xl" fontWeight="bold">
                        {t('user.detail.title')}
                    </Text>
                    <Box h="2px" bg="#089A7A" w="100px" />
                </Flex>
            </Flex>

            {/* Dane użytkownika */}
            <Flex
                flexDirection="column"
                bgColor="white"
                pb="32px"
                pt="24px"
                px="24px"
                borderRadius="4px"
            >
                <Flex flexDirection="column" pb={'32px'}>
                    <Text fontSize="lg" fontWeight="bold">
                        {t('user.detail.user_data')}
                    </Text>
                </Flex>

                <Flex direction="column" gap={4}>
                    <Box>
                        <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                            {t('user.detail.header.fullname')}
                        </Text>
                        <Input
                            value={user?.fullName || ''}
                            isReadOnly
                            bg="gray.50"
                        />
                    </Box>
                    <Box>
                        <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                            {t('user.detail.header.email')}
                        </Text>
                        <Input
                            value={user?.email || ''}
                            isReadOnly
                            bg="gray.50"
                        />
                    </Box>
                    <Box>
                        <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                            {t('user.detail.header.address')}
                        </Text>
                        <Input
                            value={user?.address || ''}
                            isReadOnly
                            bg="gray.50"
                        />
                    </Box>
                    <Box>
                        <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                            {t('user.detail.header.additional_info')}
                        </Text>
                        <Textarea
                            value={user?.additionalInfo || ''}
                            isReadOnly
                            bg="gray.50"
                            rows={3}
                        />
                    </Box>
                    <Box>
                        <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                            {t('user.detail.header.payment_integration')}
                        </Text>
                        <Input
                            value={user?.hasPaymentIntegration ? t('yes') : t('no')}
                            isReadOnly
                            bg="gray.50"
                        />
                    </Box>
                </Flex>
            </Flex>

            {/* Lista wydarzeń użytkownika */}
            <Flex
                flexDirection="column"
                bgColor="white"
                pb="32px"
                pt="24px"
                px="24px"
                borderRadius="4px"
            >
                <Flex flexDirection="column" pb={'32px'}>
                    <Text fontSize="xl" fontWeight="bold">
                        {t('user.events.title')}
                    </Text>
                </Flex>

                {eventsLoading ? (
                    <Flex justify="center" align="center" minH="200px">
                        <Spinner size="xl" />
                    </Flex>
                ) : (
                    <Box w="100%" maxW="7xl" mx="auto">
                        {/* Nagłówki tabeli */}
                        <Box
                            display="grid"
                            gridTemplateColumns="100px 1fr 150px 150px 120px 100px"
                            gap="16px"
                            mb="8px"
                        >
                            <Box
                                bg="brand.500"
                                borderRadius="0px"
                                p="12px"
                                display="flex"
                                alignItems="center"
                                fontSize="14px"
                                fontWeight="400"
                                color="white"
                                cursor="pointer"
                                onClick={() => handleSort('id')}
                                boxShadow="sm"
                            >
                                <Flex align="center" gap={1}>
                                    ID
                                    {renderSortIcon('id')}
                                </Flex>
                            </Box>
                            <Box
                                bg="brand.500"
                                borderRadius="0px"
                                p="12px"
                                display="flex"
                                alignItems="center"
                                fontSize="14px"
                                fontWeight="400"
                                color="white"
                                cursor="pointer"
                                onClick={() => handleSort('title')}
                                boxShadow="sm"
                            >
                                <Flex align="center" gap={1}>
                                    Tytuł
                                    {renderSortIcon('title')}
                                </Flex>
                            </Box>
                            <Box
                                bg="brand.500"
                                borderRadius="0px"
                                p="12px"
                                display="flex"
                                alignItems="center"
                                fontSize="14px"
                                fontWeight="400"
                                color="white"
                                cursor="pointer"
                                onClick={() => handleSort('address')}
                                boxShadow="sm"
                            >
                                <Flex align="center" gap={1}>
                                    Adres
                                    {renderSortIcon('address')}
                                </Flex>
                            </Box>
                            <Box
                                bg="brand.500"
                                borderRadius="0px"
                                p="12px"
                                display="flex"
                                alignItems="center"
                                fontSize="14px"
                                fontWeight="400"
                                color="white"
                                cursor="pointer"
                                onClick={() => handleSort('startDate')}
                                boxShadow="sm"
                            >
                                <Flex align="center" gap={1}>
                                    Data rozpoczęcia
                                    {renderSortIcon('startDate')}
                                </Flex>
                            </Box>
                            <Box
                                bg="brand.500"
                                borderRadius="0px"
                                p="12px"
                                display="flex"
                                alignItems="center"
                                fontSize="14px"
                                fontWeight="400"
                                color="white"
                                cursor="pointer"
                                onClick={() => handleSort('participants')}
                                boxShadow="sm"
                            >
                                <Flex align="center" gap={1}>
                                    Uczestnicy
                                    {renderSortIcon('participants')}
                                </Flex>
                            </Box>
                            <Box
                                bg="brand.500"
                                borderRadius="0px"
                                p="12px"
                                display="flex"
                                alignItems="center"
                                fontSize="14px"
                                fontWeight="400"
                                color="white"
                                cursor="default"
                                boxShadow="sm"
                            >
                                Akcje
                            </Box>
                        </Box>

                        {/* Wiersze tabeli */}
                        {!eventsLoading && events.length === 0 ? (
                            <VStack align="center" gap={'48px'} p={'48px'}>
                                <Text fontFamily={'Poppins'} fontWeight={'500'} fontSize={'20px'} color="#363636">
                                    {t('event.not_found')}
                                </Text>
                            </VStack>
                        ) : (
                            events.map(event => (
                                <Box
                                    key={event.id}
                                    display="grid"
                                    gridTemplateColumns="100px 1fr 150px 150px 120px 100px"
                                    gap="16px"
                                    bg="transparent"
                                    p="0"
                                    mb="12px"
                                    fontFamily="Inter"
                                    fontSize="14px"
                                    fontWeight="400"
                                >
                                    <Box bg="white" borderRadius="0px" p="12px" display="flex" alignItems="center">
                                        <Text>{event.id?.substring(0, 8) || '-'}</Text>
                                    </Box>

                                    <Box bg="white" borderRadius="0px" p="12px" display="flex" alignItems="center">
                                        <Text fontWeight="500">{event.title}</Text>
                                    </Box>

                                    <Box bg="white" borderRadius="0px" p="12px" display="flex" alignItems="center">
                                        <Text>{event.address || '-'}</Text>
                                    </Box>

                                    <Box bg="white" borderRadius="0px" p="12px" display="flex" alignItems="center">
                                        <Text>
                                            {event.startDate ? new Date(event.startDate).toLocaleDateString('pl-PL') : '-'}
                                        </Text>
                                    </Box>

                                    <Box bg="white" borderRadius="0px" p="12px" display="flex" alignItems="center">
                                        <Text>{event.participantsCount || 0} / {event.maxParticipants || 0}</Text>
                                    </Box>

                                    <Box bg="white" borderRadius="0px" p="8px" display="flex" justifyContent="center" gap={2} alignItems="center">
                                        <Tooltip label={t('action.view')}>
                                            <IconButton
                                                icon={<FiEye />}
                                                aria-label={t('action.view')}
                                                size="sm"
                                                onClick={() => window.open(`/events/${event.id}/preview`, '_blank')}
                                                bg="white"
                                                color="#0A4725"
                                            />
                                        </Tooltip>
                                    </Box>
                                </Box>
                            ))
                        )}
                    </Box>
                )}

                {/* Paginacja */}
                {totalPages > 1 && (
                    <Flex justify="center" gap={'4px'} my={'48px'}>
                        <Button
                            bgColor={'#0A4725'}
                            _hover={{ bgColor: '#0A4725' }}
                            borderRadius={0}
                            onClick={() => setCurrentPage(1)}
                            w={'40px'}
                            h={'40px'}
                        >
                            <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5 9L0.999999 5L5 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </Button>
                        {Array.from({ length: totalPages }, (_, idx) => (
                            <Button
                                fontFamily={'Poppins'}
                                fontWeight={'400'}
                                fontSize={'16px'}
                                bgColor={'#E1E1E1'}
                                _hover={{ bgColor: '#E1E1E1' }}
                                borderRadius={0}
                                borderColor={'#0A4725'}
                                border={currentPage === idx + 1 ? "2px" : "0"}
                                key={idx + 1}
                                onClick={() => setCurrentPage(idx + 1)}
                                w={'40px'}
                                h={'40px'}
                            >
                                {idx + 1}
                            </Button>
                        ))}
                        <Button
                            bgColor={'#0A4725'}
                            _hover={{ bgColor: '#0A4725' }}
                            borderRadius={0}
                            onClick={() => setCurrentPage(totalPages)}
                            w={'40px'}
                            h={'40px'}
                        >
                            <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 1L5 5L1 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </Button>
                    </Flex>
                )}
            </Flex>
        </Box>
    );
};

export default UserDetailPage;
